const {
  MARKETING_CAMPAIGNS,
  MARKETING_MEDIA,
  MARKETING_SOURCES,
} = require("../data/marketing-campaigns")

const CAMPAIGN_VALUE_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/
const ADMITTED_UTM_KEYS = Object.freeze([
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
])
const ATTRIBUTION_STORAGE_KEY = "kalpilli_campaign_attribution_v1"
const ATTRIBUTION_TTL_MS = 2 * 60 * 60 * 1000
const QUARANTINED_STORAGES = new WeakSet()
const TRACKING_QUERY_KEYS = Object.freeze([
  ...ADMITTED_UTM_KEYS,
  "utm_term",
  "gclid",
  "fbclid",
  "msclkid",
])

function getBrowserSessionStorage(target) {
  try {
    return target && target.sessionStorage ? target.sessionStorage : null
  } catch (_) {
    return null
  }
}

function removeStoredAttribution(storage) {
  try {
    if (storage) storage.removeItem(ATTRIBUTION_STORAGE_KEY)
    return Boolean(storage)
  } catch (_) {
    return false
  }
}

function normalizeCampaignValue(rawValue) {
  if (typeof rawValue !== "string") return null
  if (/[^\x20-\x7e]/.test(rawValue)) return null
  const normalized = rawValue
    .replace(/^ +| +$/g, "")
    .toLowerCase()
    .replace(/_/g, "-")
    .replace(/-+/g, "-")
  const hasLongDigitRun = /\d{8,}/.test(normalized)
  const looksLikePhone = /(?:^|-)(?:\d-?){9,}\d(?:-|$)/.test(normalized)
  if (
    !CAMPAIGN_VALUE_PATTERN.test(normalized) ||
    hasLongDigitRun ||
    looksLikePhone
  )
    return null
  return normalized
}

function parseCampaignInput(search = "", registry = MARKETING_CAMPAIGNS) {
  const params = new URLSearchParams(search)
  const hasAdmittedInput = ADMITTED_UTM_KEYS.some(
    key => params.getAll(key).length > 0
  )
  if (!hasAdmittedInput) return { status: "absent" }

  const counts = Object.fromEntries(
    ADMITTED_UTM_KEYS.map(key => [key, params.getAll(key).length])
  )
  if (
    counts.utm_source !== 1 ||
    counts.utm_medium !== 1 ||
    counts.utm_campaign !== 1 ||
    counts.utm_content > 1
  ) {
    return { status: "invalid" }
  }

  const source = normalizeCampaignValue(params.get("utm_source"))
  const medium = normalizeCampaignValue(params.get("utm_medium"))
  const campaign = normalizeCampaignValue(params.get("utm_campaign"))
  const content = counts.utm_content
    ? normalizeCampaignValue(params.get("utm_content"))
    : null
  if (!source || !medium || !campaign || (counts.utm_content && !content)) {
    return { status: "invalid" }
  }
  if (
    !MARKETING_SOURCES.includes(source) ||
    !MARKETING_MEDIA.includes(medium)
  ) {
    return { status: "invalid" }
  }

  const entry = registry.find(
    item =>
      item.status === "active" &&
      item.source === source &&
      item.medium === medium &&
      item.campaign === campaign
  )
  if (!entry || (content && !entry.contents.includes(content))) {
    return { status: "invalid" }
  }

  return {
    status: "valid",
    value: {
      source,
      medium,
      campaign,
      ...(content ? { content } : {}),
    },
  }
}

function isRegisteredRecord(record, now, registry) {
  if (
    !record ||
    typeof record !== "object" ||
    Array.isArray(record) ||
    record.version !== 1 ||
    !Number.isFinite(record.capturedAt) ||
    !Number.isFinite(record.expiresAt) ||
    record.capturedAt < 0 ||
    record.capturedAt > now ||
    record.expiresAt !== record.capturedAt + ATTRIBUTION_TTL_MS ||
    record.expiresAt <= now
  )
    return false

  const hasContent = Object.prototype.hasOwnProperty.call(record, "content")
  const expectedKeys = [
    "version",
    "source",
    "medium",
    "campaign",
    ...(hasContent ? ["content"] : []),
    "capturedAt",
    "expiresAt",
  ]
  const recordKeys = Object.keys(record)
  if (
    recordKeys.length !== expectedKeys.length ||
    recordKeys.some(key => !expectedKeys.includes(key))
  )
    return false

  const query = new URLSearchParams({
    utm_source: record.source,
    utm_medium: record.medium,
    utm_campaign: record.campaign,
    ...(hasContent ? { utm_content: record.content } : {}),
  })
  const parsed = parseCampaignInput(`?${query}`, registry)
  return (
    parsed.status === "valid" &&
    record.source === parsed.value.source &&
    record.medium === parsed.value.medium &&
    record.campaign === parsed.value.campaign &&
    hasContent ===
      Object.prototype.hasOwnProperty.call(parsed.value, "content") &&
    (!hasContent || record.content === parsed.value.content)
  )
}

function readStoredAttribution(
  storage,
  now = Date.now(),
  registry = MARKETING_CAMPAIGNS
) {
  try {
    if (storage && QUARANTINED_STORAGES.has(storage)) {
      removeStoredAttribution(storage)
      return null
    }
    const raw = storage && storage.getItem(ATTRIBUTION_STORAGE_KEY)
    if (raw === null) return null
    const record = JSON.parse(raw)
    if (!isRegisteredRecord(record, now, registry)) {
      removeStoredAttribution(storage)
      return null
    }
    return {
      version: 1,
      source: record.source,
      medium: record.medium,
      campaign: record.campaign,
      ...(record.content ? { content: record.content } : {}),
      capturedAt: record.capturedAt,
      expiresAt: record.expiresAt,
    }
  } catch (_) {
    removeStoredAttribution(storage)
    return null
  }
}

function processCampaignAttribution({
  search = "",
  storage = null,
  registry = MARKETING_CAMPAIGNS,
  now = Date.now(),
  measurementEnabled = false,
} = {}) {
  if (!measurementEnabled) {
    const removed = removeStoredAttribution(storage)
    if (
      !removed &&
      storage &&
      (typeof storage === "object" || typeof storage === "function")
    ) {
      QUARANTINED_STORAGES.add(storage)
    }
    return null
  }
  const previous = readStoredAttribution(storage, now, registry)
  const parsed = parseCampaignInput(search, registry)
  if (parsed.status !== "valid") return previous
  const record = {
    version: 1,
    ...parsed.value,
    capturedAt: now,
    expiresAt: now + ATTRIBUTION_TTL_MS,
  }
  try {
    if (!storage) return null
    storage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(record))
    QUARANTINED_STORAGES.delete(storage)
    return record
  } catch (_) {
    if (
      storage &&
      (typeof storage === "object" || typeof storage === "function")
    ) {
      QUARANTINED_STORAGES.add(storage)
    }
    removeStoredAttribution(storage)
    return null
  }
}

function buildCampaignParameters(record) {
  if (!record) return { campaign_source: "direct" }
  return {
    campaign_source: record.source,
    campaign_medium: record.medium,
    campaign_name: record.campaign,
    ...(record.content ? { campaign_content: record.content } : {}),
  }
}

function buildCleanRelativeUrl(href) {
  const url = new URL(href)
  TRACKING_QUERY_KEYS.forEach(key => url.searchParams.delete(key))
  return `${url.pathname}${url.search}${url.hash}`
}

function replaceTrackingUrl(target, options = {}) {
  let url
  try {
    url = new URL(target.location.href)
  } catch (_) {
    return { hadTracking: true, cleaned: false, replaced: false }
  }
  const hadTracking = TRACKING_QUERY_KEYS.some(key => url.searchParams.has(key))
  if (!hadTracking) {
    return { hadTracking: false, cleaned: true, replaced: false }
  }
  try {
    const clean = buildCleanRelativeUrl(url.href)
    if (typeof options.navigate === "function") {
      options.navigate(clean, {
        state: target.history.state,
        replace: true,
      })
      const currentUrl = `${target.location.pathname}${target.location.search}${target.location.hash}`
      if (currentUrl !== clean) {
        return { hadTracking: true, cleaned: false, replaced: false }
      }
      return {
        hadTracking: true,
        cleaned: true,
        replaced: true,
        routeUpdateScheduled: true,
      }
    }
    target.history.replaceState(target.history.state, "", clean)
    return { hadTracking: true, cleaned: true, replaced: true }
  } catch (_) {
    return { hadTracking: true, cleaned: false, replaced: false }
  }
}

module.exports = {
  ADMITTED_UTM_KEYS,
  ATTRIBUTION_STORAGE_KEY,
  ATTRIBUTION_TTL_MS,
  CAMPAIGN_VALUE_PATTERN,
  TRACKING_QUERY_KEYS,
  buildCampaignParameters,
  buildCleanRelativeUrl,
  getBrowserSessionStorage,
  normalizeCampaignValue,
  parseCampaignInput,
  processCampaignAttribution,
  readStoredAttribution,
  removeStoredAttribution,
  replaceTrackingUrl,
}
